import { createSlice } from '@reduxjs/toolkit';
import { EditorType } from '@wix/ambassador-metasite-search-v2-search-entry/types';
import { ArticlesSection, SectionId } from '@wix/wix-help-widget-common/homepage';

import { loadHomepageContent } from '../thunks/homepage.thunk';
import { loadRecommendedArticles } from '../thunks/recommendedArticles.thunk';
import { IHomepageSectionState, IHomepageState } from './types';

const createEmptySectionState = <T>(): IHomepageSectionState<T> => ({
  data: null,
  loading: false,
  error: null,
});

const initialState: IHomepageState = {
  scrolledToTop: true,
  content: createEmptySectionState(),
  articles: createEmptySectionState(),
  reportABugFlowStarted: false,
  showReportABugConfirmationModal: false,
  reportABugPosition: 0,
  searchConfig: {},
};

export const homepageSlice = createSlice({
  name: 'homepage',
  initialState,
  reducers: {
    setScrolledToTop: (state, action) => {
      state.scrolledToTop = action.payload;
    },
    resetRecommendedArticles: (state) => {
      state.articles.error = null;
      state.articles.loading = false;
      state.articles.data = null;
    },
    reportABugFlowStarted: (state, action) => {
      state.reportABugFlowStarted = action.payload;
    },
    setShowReportABugConfirmationModal: (state, action) => {
      state.showReportABugConfirmationModal = action.payload;
    },
    setReportABugPosition: (state, action) => {
      state.reportABugPosition = action.payload;
    },
    setReportABugText: (state, action) => {
      state.reportABugText = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadHomepageContent.pending, (state) => {
      state.content.error = null;
      state.content.loading = true;
    });

    builder.addCase(loadHomepageContent.fulfilled, (state, action) => {
      state.content.error = null;
      state.content.loading = false;
      state.content.data = action.payload;
      const articlesSection = action.payload.sections.find(
        (section) => section.sectionId === SectionId.Articles,
      ) as ArticlesSection;
      const recommendedArticles = articlesSection?.articles || [];
      if (recommendedArticles.length) {
        state.articles.data = { articles: recommendedArticles };
        state.articles.loading = false;
        state.articles.error = null;
      }
      const searchConfig = action.payload.header?.searchConfig;
      if (searchConfig) {
        state.searchConfig = {
          ...searchConfig,
          filters: {
            ...searchConfig.filters,
            editorType: searchConfig.filters?.editorType as EditorType,
          },
        };
      }
    });

    builder.addCase(loadHomepageContent.rejected, (state, action) => {
      state.content.error = action.error as string;
      state.content.loading = false;
    });

    builder.addCase(loadRecommendedArticles.pending, (state) => {
      state.articles.error = null;
      state.articles.loading = true;
    });

    builder.addCase(loadRecommendedArticles.fulfilled, (state, action) => {
      state.articles.error = null;
      state.articles.loading = false;
      state.articles.data = action.payload;
    });

    builder.addCase(loadRecommendedArticles.rejected, (state, action) => {
      state.articles.error = action.error as string;
      state.articles.loading = false;
    });
  },
});
